"use client";

import { CcResponsivePoster } from "@/components/services/shared/CcResponsivePoster";
import { useEffect, useRef } from "react";

import { useCcPrefersReducedMotion } from "@/lib/use-cc-prefers-reduced-motion";

type CcFortnoxVideoProps = {
  src: string;
  /** Lighter 960×540 clip for phones. */
  srcSmall?: string;
  poster: string;
  className?: string;
  /** Rendered width of the clip, so the still frame is fetched at the right size. */
  sizes: string;
  /** The hero: still frame preloaded, clip fetched once the page has loaded. */
  priority?: boolean;
};

/**
 * Run once the page has loaded and the browser is idle, so the clip never
 * competes with the page's own resources.
 */
function afterLoad(callback: () => void) {
  let idle = 0;
  let timer = 0;
  const start = () => {
    if (typeof window.requestIdleCallback === "function") {
      idle = window.requestIdleCallback(callback, { timeout: 2500 });
    } else {
      timer = window.setTimeout(callback, 1200);
    }
  };
  if (document.readyState === "complete") start();
  else window.addEventListener("load", start, { once: true });
  return () => {
    window.removeEventListener("load", start);
    if (idle) window.cancelIdleCallback(idle);
    window.clearTimeout(timer);
  };
}

/**
 * Muted, looping background clip over an optimised still frame (next/image,
 * so phones get a small WebP/AVIF). The clip is only fetched near the
 * viewport, plays while on screen and pauses when scrolled away. With
 * reduced motion the still frame stays.
 */
export function CcFortnoxVideo({
  src,
  srcSmall,
  poster,
  className,
  sizes,
  priority = false,
}: CcFortnoxVideoProps) {
  const ref = useRef<HTMLVideoElement>(null);
  const reduced = useCcPrefersReducedMotion();

  useEffect(() => {
    const video = ref.current;
    if (!video || reduced) return;
    let inView = false;
    let ready = !priority;
    const tryPlay = () => {
      if (!ready || !inView || document.visibilityState !== "visible") return;
      if (!video.getAttribute("src")) {
        const small = window.matchMedia("(max-width: 810px)").matches;
        video.src = small && srcSmall ? srcSmall : src;
      }
      void video.play().catch(() => {});
    };
    const cancelLoad = priority
      ? afterLoad(() => {
          ready = true;
          tryPlay();
        })
      : () => {};
    const observer = new IntersectionObserver(
      ([entry]) => {
        inView = entry.isIntersecting;
        if (inView) tryPlay();
        else video.pause();
      },
      { rootMargin: priority ? "0px" : "200px 0px" },
    );
    observer.observe(video);
    video.addEventListener("canplay", tryPlay);
    document.addEventListener("visibilitychange", tryPlay);
    return () => {
      cancelLoad();
      observer.disconnect();
      video.removeEventListener("canplay", tryPlay);
      document.removeEventListener("visibilitychange", tryPlay);
    };
  }, [src, srcSmall, reduced, priority]);

  return (
    <>
      <CcResponsivePoster
        className={className}
        src={poster}
        fill
        sizes={sizes}
        first={priority}
      />
      <video
        ref={ref}
        className={className}
        muted
        loop
        playsInline
        preload="none"
        aria-hidden="true"
        tabIndex={-1}
      />
    </>
  );
}
